import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  IconButton, Typography, Box, Card, CardContent,
  Select, MenuItem, Divider, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  TaskData, TaskStatus, TaskPriority, TaskCategory,
  STATUS_LABELS, PRIORITY_LABELS, CATEGORY_LABELS,
} from '../../types/task';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

function formatDialogDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY_LABELS[d.getDay()]}）`;
}

const PRIORITY_BADGE_COLOR: Record<TaskPriority, string> = {
  [TaskPriority.HIGH]:   '#e53935',
  [TaskPriority.MEDIUM]: '#fb8c00',
  [TaskPriority.LOW]:    '#9e9e9e',
};

const CATEGORY_CHIP_COLOR: Record<TaskCategory, { bg: string; text: string }> = {
  [TaskCategory.ENTRY]:            { bg: '#e3f2fd', text: '#1565c0' },
  [TaskCategory.ES_SUBMISSION]:    { bg: '#fff3e0', text: '#e65100' },
  [TaskCategory.APTITUDE_TEST]:    { bg: '#f3e5f5', text: '#6a1b9a' },
  [TaskCategory.FIRST_INTERVIEW]:  { bg: '#e8f5e9', text: '#2e7d32' },
  [TaskCategory.SECOND_INTERVIEW]: { bg: '#e0f7fa', text: '#006064' },
  [TaskCategory.FINAL_INTERVIEW]:  { bg: '#fce4ec', text: '#880e4f' },
  [TaskCategory.OFFER]:            { bg: '#fff8e1', text: '#f57f17' },
};

interface Props {
  open:           boolean;
  date:           string | null;
  tasks:          TaskData[];
  onClose:        () => void;
  onEdit:         (task: TaskData) => void;
  onStatusChange: (id: number, status: TaskStatus) => void;
}

const TaskCard: React.FC<{
  task: TaskData;
  onEdit: (task: TaskData) => void;
  onStatusChange: (id: number, status: TaskStatus) => void;
}> = ({ task, onEdit, onStatusChange }) => {
  const isCompleted = task.status === TaskStatus.COMPLETED;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        opacity: isCompleted ? 0.6 : 1,
      }}
    >
      <CardContent sx={{ pb: '12px !important', px: 2, pt: 2 }}>

        {/* 会社名 + カテゴリ */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {task.company_name ?? ''}
          </Typography>
          <Box
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: CATEGORY_CHIP_COLOR[task.category]?.bg ?? '#f5f5f5',
              color:   CATEGORY_CHIP_COLOR[task.category]?.text ?? '#555',
              whiteSpace: 'nowrap',
            }}
          >
            {CATEGORY_LABELS[task.category]}
          </Box>
        </Box>

        {/* 優先度 + タイトル */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <Box
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.75,
              bgcolor: PRIORITY_BADGE_COLOR[task.priority],
              color: 'white',
              flexShrink: 0,
            }}
          >
            {PRIORITY_LABELS[task.priority]}
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              textDecoration: isCompleted ? 'line-through' : 'none',
              color: isCompleted ? 'text.disabled' : 'text.primary',
              lineHeight: 1.4,
            }}
          >
            {task.title}
          </Typography>
        </Box>

        {/* 時刻 */}
        {task.scheduled_time_start && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {task.scheduled_time_start}
            </Typography>
          </Box>
        )}

        {/* 場所 */}
        {task.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {task.location}
            </Typography>
          </Box>
        )}

        {/* ステータス + 編集ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Select
            size="small"
            value={task.status}
            onChange={(e) => onStatusChange(task.id, e.target.value as TaskStatus)}
            sx={{ fontSize: '0.78rem', height: 30, minWidth: 100 }}
          >
            {Object.values(TaskStatus).map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>
                {STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </Select>
          <IconButton
            size="small"
            onClick={() => onEdit(task)}
            sx={{ color: 'text.secondary' }}
            title="編集"
          >
            <EditIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>

      </CardContent>
    </Card>
  );
};

export const TaskDateDialog: React.FC<Props> = ({
  open, date, tasks, onClose, onEdit, onStatusChange,
}) => {
  const [completedOpen, setCompletedOpen] = useState(false);

  const activeTasks    = tasks.filter((t) => t.status !== TaskStatus.COMPLETED);
  const completedTasks = tasks.filter((t) => t.status === TaskStatus.COMPLETED);

  const handleEdit = (task: TaskData) => {
    onClose();
    onEdit(task);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { m: { xs: 1, sm: 3 }, borderRadius: 3 } }}
    >
      {/* ヘッダー */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
            {date ? formatDialogDate(date) : ''}のタスク
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* コンテンツ */}
      <DialogContent sx={{ pt: 0.5 }}>
        {tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography variant="body2">この日のタスクはありません</Typography>
          </Box>
        ) : (
          <>
            {/* 未完了タスク */}
            {activeTasks.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: completedTasks.length > 0 ? 2 : 0 }}>
                {activeTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={handleEdit}
                    onStatusChange={onStatusChange}
                  />
                ))}
              </Box>
            )}

            {/* 完了済みセクション */}
            {completedTasks.length > 0 && (
              <Box>
                {activeTasks.length > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Box
                  onClick={() => setCompletedOpen((prev) => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    mb: completedOpen ? 1.5 : 0,
                    color: 'text.secondary',
                    userSelect: 'none',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {completedOpen
                    ? <ExpandLessIcon fontSize="small" />
                    : <ExpandMoreIcon fontSize="small" />}
                  <Typography variant="body2" fontWeight={600}>
                    完了済み（{completedTasks.length}件）
                  </Typography>
                </Box>
                <Collapse in={completedOpen}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {completedTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={handleEdit}
                        onStatusChange={onStatusChange}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
